import { ApplicationError } from 'features/error-handling/ApplicationError';
import { logger } from 'features/logger/logger';
import { questionnaireProseThunk } from 'features/questionnaires/store/questionnaireProseThunk';
import {
  errorOccurred,
  setLastLoaded,
  setPageIndex,
  setShowSummary,
} from 'features/questionnaires/store/questionnaireSlice';
import type { DbTask } from '@hub/common';
import type { AppDispatch } from 'features/store/store';

// Applies a task-document snapshot to the questionnaire state for multi-window
// synchronization. The caller invokes this only when the questionnaire is
// initialized. `windowId` is the current window, so an echo of this window's own
// write skips the PROSE re-render.
export const syncQuestionnaireFromTaskSnapshot = (
  dispatch: AppDispatch,
  data: DbTask,
  windowId: string
): void => {
  const asyncDispatch = async () => {
    const {
      fieldsData,
      contextData,
      calculationsData,
      touchedFields,
      traceClientWindowId,
      traceClientActionType,
      showSummary,
    } = data;

    dispatch(
      setLastLoaded({
        fieldsData,
        contextData,
        calculationsData,
        touchedFields,
      })
    );

    if (
      traceClientActionType !== 'screen-load' &&
      traceClientWindowId === windowId
    ) {
      logger.debug(
        `Skipping questionnaireProseThunk dispatch (update by same screen that committed change: ${traceClientWindowId})`
      );
    } else if (traceClientActionType === 'screen-page-change') {
      logger.debug(
        'Skipping questionnaireProseThunk dispatch (not needed for page change)'
      );

      if (data.pageIndex !== undefined) {
        logger.debug(
          `Synchronizing page index to: ${data.pageIndex.toString()}`
        );
        dispatch(setPageIndex(data.pageIndex));
      }
    } else if (traceClientActionType === 'screen-summary') {
      logger.debug(
        'Skipping questionnaireProseThunk dispatch (not needed for summary display)'
      );

      if (showSummary === undefined) {
        throw new Error(
          '[ASSERT FAILED] showSummary is undefined while screen-summary action was received'
        );
      }
      dispatch(setShowSummary(showSummary));
    } else if (traceClientActionType === 'screen-finish') {
      logger.debug(
        'Skipping questionnaireProseThunk dispatch (not needed for questionnaire finish)'
      );
    } else if (traceClientActionType === 'screen-post-questionnaire-enter') {
      logger.debug(
        'Post-questionnaire entered from another window — re-running PROSE to enter phase'
      );
      await dispatch(questionnaireProseThunk(fieldsData, touchedFields));
    } else {
      await dispatch(questionnaireProseThunk(fieldsData, touchedFields));
    }
  };

  asyncDispatch().catch((error: unknown) => {
    const appError = new ApplicationError('PHW-Q-001');
    appError.cause = error;

    logger.error(
      'Error in questionnaire snapshot handler (asyncDispatch)',
      undefined,
      appError
    );

    dispatch(errorOccurred(appError.toErrorData()));
  });
};
